import { supabase } from './supabase';

interface UploadOptions {
  fileName: string;
  file: File;
  folder?: string;
  onProgress?: (progress: number) => void; // Basic support, XMLHttpRequest could give actual progress
}

interface UploadResponse {
  key: string;
  publicUrl: string;
}

export const uploadToR2 = async ({ fileName, file, folder }: UploadOptions): Promise<UploadResponse> => {
  // 1. Get pre-signed URL from Edge Function
  const { data, error } = await supabase.functions.invoke('r2-upload-url', {
    body: {
      fileName,
      contentType: file.type || 'application/octet-stream',
      folder,
    },
  });

  if (error) {
    console.error('Error invoking r2-upload-url:', error);
    throw new Error('Failed to get upload URL: ' + error.message);
  }

  if (!data?.url) {
    throw new Error('Invalid response from upload service');
  }

  const { url, key, publicUrl } = data;

  // 2. Upload file directly to R2 using the pre-signed URL
  const uploadResponse = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload file to storage: ${uploadResponse.statusText}`);
  }

  // 3. Construct public URL if not provided by Edge function (requires adding to env vars later)
  // For now, assume Edge function provides the complete public URL based on its environment
  let finalUrl = publicUrl;
  if (!finalUrl) {
      // Fallback if R2_PUBLIC_DOMAIN isn't set in Edge Function yet, but this will break images.
      // We will tell the user to configure R2_PUBLIC_DOMAIN.
      finalUrl = `https://R2_PUBLIC_DOMAIN_NOT_SET/${key}`;
  }

  return { key, publicUrl: finalUrl };
};

export const deleteFromR2 = async (objectUrl: string): Promise<boolean> => {
  if (!objectUrl || !objectUrl.startsWith('http')) return false;

  const { data, error } = await supabase.functions.invoke('r2-delete-object', {
    body: {
      objectUrl,
    },
  });

  if (error) {
    console.error('Error invoking r2-delete-object:', error);
    return false;
  }

  return data?.success || false;
};
